import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Skeleton, Stack, TextField } from "@mui/material";
import useLocalStorageState from "use-local-storage-state";

import { faExclamationTriangle, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { useLocation, useParams } from "react-router-dom";

import { Icon, LoadingOverlay, Modal } from "@/components";
import { applyPersistedColumnConfig } from "@/components/Table/applyPersistedColumnConfig";

import { COLUMN_CONFIG_VERSION } from "@/constants";

import { useSearch, useTableSearch } from "@/utils";
import { toPriorityFilterItems } from "@/utils/priorityFilterOptions";
import { useTrackerDestinationLists } from "@/pages/Private/pages/List/utils/useTrackerDestinationLists";

import {
	useBulkAddCompanyToTrackerMutation,
	useBulkAddToTrackerMutation,
	useBulkRunPromptMutation,
	useCreateSlackMessageMutation,
	useDeleteListColumnMutation,
	useGetLeanListQuery,
	useLazyDownloadFilteredQuery,
	useLazyGetUnassignedQuery,
} from "@/pages/Private/redux/list/list.api";

import { Table, TableColumn } from "@/components/Table/Table";
import { useBulkFunctions } from "@/utils/userBulkFunctions";
import { Order, SortParams } from "@/types/types";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	AssignStatus,
	CustomColumnTypes,
	InclusionFilterStatus,
	InclusionStatus,
	ListSourceType,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { useCopyToClipboard } from "@/utils/useCopyToClipboard";

import { InputField } from "@/components/InputField/InputField";

import { TItem } from "@/components/SortableList/SortableList";

import { useBulkBlacklistMutation } from "@/pages/Private/redux/list/list-bulk-status.api";
import { runOrToast } from "@/utils/runOrToast";

import { TextArea } from "@/components/TextArea/TextArea";

import { leadsByIds, selectionOfLeads } from "@/components/CompanyBlacklistToggle/companyBlacklist";

import { useSnackBar } from "@/utils/useSnackbar";

import { ScrapeLevel } from "@/enum/job.enum";

import { Radio } from "@/components/Radio/Radio";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import {
	useCreateTableConfigMutation,
	useLazyGetTableConfigQuery,
	useUpdateTableConfigMutation,
} from "@/pages/Private/redux/tableConfig/tableConfig.api";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { tableConfigSelector } from "@/pages/Private/redux/tableConfig/tableConfig.slice";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { useExecuteBulkDeepResearchListMutation } from "@/pages/Private/redux/deepResearch/deepResearch.api";
import { AddToCrmModal, AddToCrmOwner } from "@/pages/Private/components/AddToCrm/AddToCrmModal";
import { useAddProspectsToCrmMutation } from "@/pages/Private/redux/crm/crm.api";
import {
	CrmEntityType,
	CrmProvider,
	CrmSelectionSource,
} from "@/pages/Private/redux/crm/crm.schema";

import { UnassignedAddLeadModal } from "./UnassignedAddLeadModal";

import { useReachOutColumnWiring } from "./useReachOutColumnWiring";
import { buildTableConfigItems } from "./buildTableConfigItems";
import {
	areTableConfigsEqual,
	getTableConfigReset,
	migrateTableConfig,
	OTHER_LISTS_MIGRATION,
} from "./migrateTableConfig";
import { assertBulkAddSucceeded } from "../../utils/bulkAddResult";
import { useTrackerAddHint } from "../../utils/useTrackerAddHint";
import { getColumnsForListType } from "./getColumnsForListType";

import { ListPerson } from "../../../List/schema/list";

import { UnassignedBlacklistModal } from "./UnassignedBlacklistModal";
import { FilterModal, IFilterWithValues } from "../FilterModal";
import { CreateCustomColumnModal } from "../CreateCustomColumnModal";

import { CustomBooleanColumn } from "../CustomColumns/CustomBooleanColumn";

import { insertOutreachColumn } from "../../utils/columns/insertOutreachColumn";
import { EditCustomColumnModalWrapper } from "../EditCustomColumnModalWrapper";
import { ColumnModal } from "../ColumnModal";
import { TrackerDestinationPicker } from "../TrackerDestinationPicker/TrackerDestinationPicker";
import { getOrderStatus } from "../../utils/column-utils";
import { getSvgIcons } from "../../../ProfileTracker/utils/getIconsByType";
import { DetailView } from "../../pages/Unassigned/components/DetailView";
import { InfoAtGoogleMapsDetailView } from "../../pages/Unassigned/components/InfoAtGoogleMapsDetailView";
import { FeaturesCodes } from "../../../Users/enum/codes";
import { CustomLinkColumn } from "../CustomColumns/CustomLinkColumn";
import { CustomNumberColumn } from "../CustomColumns/CustomNumberColumn";
import { CustomTextColumn } from "../CustomColumns/CustomTextColumn";
import { CustomTextColumnReadOnly } from "../CustomColumns/CustomTextColumnReadOnly";
import { CustomDepartmentSizeColumn } from "../CustomColumns/CustomDepartmentSizeColumn";
import { useListPersonSocket } from "../../providers/SocketContext";
import { useWebhooks } from "../../utils/useWebhooks";
import { BulkReachOutMenu } from "./components/BulkReachOutMenu";
import { ExcludeFromListConfirmation } from "./components/ExcludeFromListConfirmation";
import { useBulkReachOut } from "./hooks/useBulkReachOut";
import { useBulkStatusScope } from "./hooks/useBulkStatusScope";
import { useBlacklistAwareStatusChange } from "./hooks/useBlacklistAwareStatusChange";
import { useBulkFindPerson } from "./hooks/useBulkFindPerson";
import { useBulkDeepResearchHandlers } from "./hooks/useBulkDeepResearchHandlers";
import { useBulkCreditActions } from "./hooks/useBulkCreditActions";
import { useAddToAnotherList } from "./hooks/useAddToAnotherList";

import { UnassignedTableProps } from "./unassigned-table.types";
import { useListEmptyStateNode, useListFilterUrlSync, useTablePermissions } from "./hooks";
import { getBulkAssignParam } from "./utils/getBulkAssignParam";
import {
	currentViewFilters,
	selectAllRequestScope,
	selectAllViewFilters,
	tabAssignStatuses,
} from "./utils/getBulkStatusScope";
import { buildBulkBlacklistRequest } from "./utils/buildBulkBlacklistRequest";
import { getDownloadFilteredPayload } from "./utils/getDownloadFilteredPayload";
import { GroupedShowFilter } from "./components/GroupedShowFilter/GroupedShowFilter";
import { useEnrichAnyway } from "./components/EnrichAnyway/useEnrichAnyway";
import {
	getExclusionReasonsFromUrl,
	getInclusionStatusFromUrl,
	getPriorityFilterStatusFromUrl,
	getSearchTermFromUrl,
	getSignalFilterStatusFromUrl,
} from "./utils/urlFilterState";
import { DeepResearchModal } from "../CustomColumns/Modals/DeepResearchModal";
import { UnassignedTableActions } from "./UnassignedTableActions";
import { useSortUrlSync } from "../SortByDropdown/useSortUrlSync";

// Re-export for backward compatibility
export type { FilterUnassignedTable } from "./unassigned-table.types";

export const UnassignedTable: FunctionComponent<UnassignedTableProps> = ({
	assignStatus,
	listType,
	setUnsureCount,
	stickyHeader,
	toolbarSlot,
	onSelectionChange,
}) => {
	const { t } = useTranslation();
	const { id } = useParams();

	const { data: list, isFetching: isFetchingList } = useGetLeanListQuery({
		id: id ? +id : 0,
		assignStatus,
	});

	const location = useLocation();

	useEffect(() => {
		if (setUnsureCount) {
			setUnsureCount(list?.excludedCount ?? 0);
		}
	}, [list?.excludedCount]);

	const getFilterFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(params.get("filter") || "{}");
	};

	const [priorityFilterStatus, setPriorityFilterStatus] = useState<PriorityFilterStatus[]>(
		getPriorityFilterStatusFromUrl(location.search)
	);

	const [signalFilterStatus, setSignalFilterStatus] = useState<SignalFilterStatus[]>(
		getSignalFilterStatusFromUrl(location.search)
	);

	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);
	// The Blacklist bucket's columns reuse the Blacklist tab's wording (PREC-3594) — one vocabulary
	// for the blacklist, wherever it is shown.
	const tsContacts = useCallback((key: string) => t(`contacts.${key}`), [t]);
	const { sortingInbox, inbox: data, meta } = useAppSelector(listSelector);
	const { data: tableConfiguration } = useAppSelector(tableConfigSelector);
	const IRL_COUNTRIES_SUM = useIsFeatureActive(FeaturesCodes.IRL_COUNTRIES_SUM);
	const LEAD_SCORING = useIsFeatureActive(FeaturesCodes.LEAD_SCORING);
	const DEEP_RESEARCH = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);

	const { renderModal, selectedWebhook, setShowWebhookModal, bulkAddToWebhook } = useWebhooks();

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const { page, setPage, limit, query, setLimit, setSearchValue, searchValue } = useTableSearch(
		getSearchTermFromUrl(location.search)
	);

	const [snoozedUntil, setSnoozedUntil] = useState<Date>(new Date());
	const [customColumnTextValue, setCustomColumnTextValue] = useState<string>();
	const [customColumnNumberValue, setCustomColumnNumberValue] = useState<number | null>();
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [reportReason, setReportReason] = useState<string>("");

	const {
		selectedItems,
		selectAll,
		handleSelectAll,
		handleSelectItem,
		toggleSelectAll,
		setSelectedItems,
	} = useBulkFunctions();

	const [showAll, setShowAll] = useState<boolean>(false);
	const [selectType, setSelectType] = useState<SelectType>(SelectType.CURRENT_PAGE);

	const [prepSelectType, setPrepSelectType] = useState<SelectType>(SelectType.CURRENT_PAGE);

	const [oldData, setOldData] = useState<ListPerson[]>([]);

	const [oldSelectAll, setOldSelectAll] = useState<boolean>(false);

	const [inclusionStatus, setInclusionStatus] = useState<InclusionFilterStatus[] | undefined>(
		getInclusionStatusFromUrl(location.search, assignStatus)
	);

	// Bucket-scoped reason sub-filters of the grouped Show dropdown (PREC-3536 Phase 5),
	// encoded "STATUS:filterParam"; present only for partially selected buckets.
	const [exclusionReasons, setExclusionReasons] = useState<string[]>(() =>
		getExclusionReasonsFromUrl(location.search)
	);

	// Status moves that take blacklisted leads off the blacklist ask first (PREC-2548).
	const {
		changeStatus: bulkChangeStatus,
		isLoading: bulkChangeStatusIsLoading,
		leaveBlacklistModal,
	} = useBlacklistAwareStatusChange({ data });
	const [addProspectsToCrm, { isLoading: isAddToCrmLoading }] = useAddProspectsToCrmMutation();
	const [bulkBlacklist] = useBulkBlacklistMutation();
	const [createSlackMessage] = useCreateSlackMessageMutation();

	const [showAddToTrackerModal, setShowAddToTrackerModal] = useState<boolean>(false);
	const [selectedCrmProvider, setSelectedCrmProvider] = useState<CrmProvider>();
	const [showNewColumn, setShowNewColumn] = useState<boolean>(false);
	const [showAddLeadModal, setShowAddLeadModal] = useState<boolean>(false);
	const [editableColumn, setEditableColumn] = useState<TItem>();
	const [showEditColumn, setShowEditColumn] = useState<boolean>(false);

	const [showDeleteColumn, setShowDeleteColumn] = useState<boolean>(false);
	const [showBlacklistModal, setShowBlacklistModal] = useState<boolean>(false);
	const [showSnoozeModal, setShowSnoozeModal] = useState<boolean>(false);
	const [showUnsnoozeModal, setShowUnsnoozeModal] = useState<boolean>(false);
	const [showExcludeFromListModal, setShowExcludeFromListModal] = useState<boolean>(false);
	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
	const [showAddToListModal, setShowAddToListModal] = useState<boolean>(false);
	const [showCheckLasterModal, setShowCheckLasterModal] = useState<boolean>(false);
	const [showRevertModal, setShowRevertModal] = useState<boolean>(false);
	const [showUnarchiveModal, setShowUnarchiveModal] = useState<boolean>(false);
	const [unarchiveTarget, setUnarchiveTarget] = useState<AssignStatus>(AssignStatus.DRAFT);
	const [showRevertCampaignAssignmentModal, setShowRevertCampaignAssignmentModal] =
		useState<boolean>(false);
	const [showDeepResearchModal, setShowDeepResearchModal] = useState<boolean>(false);
	const [showRunAiSnippetsModal, setShowRunAiSnippetsModal] = useState<boolean>(false);
	const [showDetailView, setShowDetailView] = useState<boolean>(false);
	const [selectedListPerson, setSelectedListPerson] = useState<ListPerson | undefined>();

	// Outreach column wiring (copywriting socket + detail-panel open handlers) — extracted to keep
	// this component shrinking. `openChatFor` opens the panel straight on the Chat view.
	const {
		openChatFor,
		handleOpenDetailForRow,
		handleOpenChatForRow,
		handleOpenResearchTab,
		handleRowClick,
	} = useReachOutColumnWiring({
		rows: data,
		showDetailView,
		setSelectedListPerson,
		setShowDetailView,
		setSearchValue,
	});

	const [showReportRecordModal, setShowReportRecordModal] = useState<boolean>(false);
	const [addEntireCompanyToBlacklist, setAddEntireCompanyToBlacklist] = useState<boolean>(false);
	const [selectedItemForConfirmation, setSelectedItemForConfirmation] = useState<ListPerson | null>(
		null
	);

	const [selectedTracker, setSelectedTracker] = useState<AutoCompleteItem | null>();
	// Why Add has nothing to send, instead of a silent no-op (PREC-4497).
	const trackerAddHint = useTrackerAddHint();
	const [filter, setFilter] = useState<IFilterWithValues | undefined>(getFilterFromUrl());
	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

	// When a search query returns no rows while modal filters are active, hint that the
	// filters may be the reason and offer to re-run the search by query alone. Clearing the
	// modal filter re-triggers the existing fetch effect and keeps the query + status toggles.
	const showFilterWarning = !!query && !!filter && Object.keys(filter).length > 0;
	const handleSearchWithoutFilters = () => setFilter(undefined);

	// PREC-3542: designed empty states (never had leads / filtered out / all clean) for the
	// table's empty slot, falling back to the existing filter warning / default message.
	const noDataNode = useListEmptyStateNode({
		listType,
		assignStatus,
		query,
		filter,
		signalFilterStatus,
		priorityFilterStatus,
		showFilterWarning,
		onSearchWithoutFilters: handleSearchWithoutFilters,
		setPage,
		setInclusionStatus,
	});
	const [showEdit, setShowEdit] = useState<{ id: number; name: string; columnConfigId?: number }>();
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(`column-settings-${id}`, {
		defaultValue: [],
	});
	const [columnMigrations, setColumnMigrations] = useLocalStorageState<string[]>(
		`column-settings-migrations-${id}`,
		{ defaultValue: [] }
	);
	// Force-migrates a saved column config to the current default layout when the layout version is
	// bumped (PREC-3567). Persisted per list so each list migrates once.
	const [columnConfigVersion, setColumnConfigVersion] = useLocalStorageState<number>(
		`column-settings-version-${id}`,
		{ defaultValue: 0 }
	);

	useEffect(() => {
		if (selectAll && selectType === SelectType.ALL && selectedItems.length !== data.length) {
			setSelectType(SelectType.CURRENT_PAGE);
		}
	}, [selectedItems, selectType, data.length, selectAll]);

	useEffect(() => {
		onSelectionChange?.(selectedItems);
	}, [selectedItems, onSelectionChange]);

	// The scope every select-all request carries: the API re-derives the selection from it.
	const bulkStatusScope = useBulkStatusScope({
		selectType,
		filter,
		inclusionStatus,
		exclusionReasons,
		priorityFilterStatus,
		signalFilterStatus,
		searchValue: query,
		assignStatus,
	});

	// Keeps every active filter in the query string (shareable/reload-safe) — see the hook for why
	// this must stay a single writer.
	useListFilterUrlSync({
		assignStatus,
		filter,
		searchTerm: query,
		inclusionStatus,
		exclusionReasons,
		priorityFilterStatus,
		signalFilterStatus,
	});

	const [deleteColumn, { isLoading: deleteLoading }] = useDeleteListColumnMutation();
	const [getUnassigned, { isLoading, isFetching }] = useLazyGetUnassignedQuery();

	const [downloadSelected, { isFetching: downloadIsFetching }] = useLazyDownloadFilteredQuery();
	const [bulkRunPrompt, { isLoading: bulkRunPromptIsLoading }] = useBulkRunPromptMutation();
	const [, { isLoading: executeBulkDeepResearchListIsLoading }] =
		useExecuteBulkDeepResearchListMutation({
			fixedCacheKey: "execute-bulk-deep-research-list",
		});
	const [createConfig, { isLoading: isCreatingLoading }] = useCreateTableConfigMutation();
	const [updateConfig, { isLoading: isUpdatingLoading }] = useUpdateTableConfigMutation();

	const [selectedCampaignAndProspect, setSelectedCampaignAndProspect] = useState<
		{
			campaignId: number | string;
			listPersonId: number;
			type?: "internal" | "lemlist";
		}[]
	>([]);

	const [bulkAddToTracker, { isLoading: bulkAddToTrackerIsLoading }] =
		useBulkAddToTrackerMutation();
	const [bulkAddCompanyToTracker, { isLoading: bulkAddCompanyToTrackerIsLoading }] =
		useBulkAddCompanyToTrackerMutation();

	const [addOrganizationOnly, setAddOrganizationOnly] = useState<boolean>(false);

	const { sortBy: urlSortBy, sortOrder: urlSortOrder, onSort: applySortToUrl } = useSortUrlSync();

	// Sort is URL-driven (PREC-3544): write it to the URL (source of truth) and let `useSortUrlSync`
	// mirror it into `sortingInbox`. This keeps the sort shareable/reload-safe yet scoped to this
	// route, so it doesn't carry over to the Messages surfaces.
	const handleSorting = (sortParams: SortParams) => {
		setPage(1);
		applySortToUrl(sortParams);
	};

	// "Enrich anyway" recovery for title-excluded leads (PREC-3536 Phase 5).
	const { footerEnrichAnyway, openEnrichAnywayForRow, retryRow, renderEnrichAnywayModals } =
		useEnrichAnyway({
			listId: id ? +id : undefined,
			data,
			selectedItems,
			onSortByLastEnriched: () =>
				handleSorting({ sortBy: "list_person.updatedAt", sortOrder: Order.DESC }),
			onNotify: handleOpenSnackbar,
		});

	const { clearActualData } = useListPersonSocket();

	useEffect(() => {
		return () => {
			clearActualData();
		};
	}, []);

	useEffect(() => {
		setPage(1);
	}, [query]);

	const { setSearchValue: setTrackerSearchValue, query: trackerQuery } = useSearch();

	const {
		lists: combinedTrackerAndJobChangeLists,
		isLoading: isLoadingTrackerLists,
		isFetching: isFetchingTrackerLists,
	} = useTrackerDestinationLists({ searchValue: trackerQuery });

	const selectedListIsJobChange =
		combinedTrackerAndJobChangeLists.find((list) => list.id === selectedTracker?.id)?.sourceType ===
		ListSourceType.JOB_CHANGE;

	const [getTableConfig, { isLoading: isLoadingConfig }] = useLazyGetTableConfigQuery();

	useEffect(() => {
		if (id) {
			getTableConfig({ id: +id, source: "list" });
		}
	}, [id]);

	const resetTableConfig = (force = false) => {
		if (!id || isLoadingConfig || isCreatingLoading) {
			return;
		}

		const resetConfig = data?.length
			? getTableConfigReset(defaultTableColumns, tableConfig, force)
			: undefined;

		if (resetConfig) {
			if (resetConfig.length > 0) {
				setTableConfig(resetConfig);
				createConfig({ listId: +id, config: resetConfig });
			}

			return;
		}

		const migrated = migrateTableConfig(tableConfig, columnMigrations);

		if (migrated !== tableConfig) {
			setTableConfig(migrated);
			setColumnMigrations([...columnMigrations, OTHER_LISTS_MIGRATION]);
		}
	};

	useEffect(() => {
		resetTableConfig();
	}, [
		tableConfiguration,
		isLoadingConfig,
		isCreatingLoading,
		id,
		data,
		tableConfig,
		columnMigrations,
	]);

	// Force-reset the persisted column config to the new defaults once when the layout version is
	// bumped, so existing users adopt the improved default order/visibility (PREC-3567).
	useEffect(() => {
		if (!id || isLoadingConfig || isCreatingLoading) {
			return;
		}

		if (columnConfigVersion !== COLUMN_CONFIG_VERSION && data && data.length > 0) {
			resetTableConfig(true);
			setColumnConfigVersion(COLUMN_CONFIG_VERSION);
		}
	}, [id, isLoadingConfig, isCreatingLoading, columnConfigVersion, data]);

	// Row fetch. The slice clears + re-claims `inbox` on each request's `matchPending` and ignores
	// stale responses by requestId, so this effect just fires the request — no manual `clearInbox()`
	// or lazy-query `abort()` (the abort suppressed the follow-up request on a client-side tab switch).
	useEffect(() => {
		getUnassigned({
			page,
			limit,
			searchValue: query,
			sorting: sortingInbox,
			assignStatus: tabAssignStatuses(assignStatus),
			listId: id ? +id : undefined,
			filter,
			inclusionFilterStatus: inclusionStatus ?? undefined,
			exclusionReasons: exclusionReasons.length > 0 ? exclusionReasons : undefined,
			priorityFilterStatus: priorityFilterStatus ?? undefined,
			signalFilterStatus: signalFilterStatus ?? undefined,
		});
	}, [
		assignStatus,
		filter,
		getUnassigned,
		id,
		inclusionStatus,
		exclusionReasons,
		limit,
		page,
		query,
		sortingInbox,
		priorityFilterStatus,
		signalFilterStatus,
	]);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleSetSelectType = (selectType: SelectType) => {
		handleSelectAll(data?.map((item) => item.id) || []);
		toggleSelectAll();
		setShowAll(false);
		setSelectType(selectType);

		setPrepSelectType(SelectType.CURRENT_PAGE);
	};

	const handleSelectCampaignAndProspect = useCallback(
		(
			selectedCampaignId: number | string,
			selectedListPersonId: number,
			type?: "internal" | "lemlist"
		) => {
			if (!selectedItems.includes(selectedListPersonId)) {
				handleSelectItem(selectedListPersonId);
			}

			const withoutRow = selectedCampaignAndProspect.filter(
				(c) => c.listPersonId !== selectedListPersonId
			);

			setSelectedCampaignAndProspect([
				...withoutRow,
				{
					campaignId: selectedCampaignId,
					listPersonId: selectedListPersonId,
					type,
				},
			]);
		},
		[selectedCampaignAndProspect, handleSelectItem, selectedItems]
	);

	const handleExport = async () => {
		try {
			const downloadPayload = getDownloadFilteredPayload({
				page,
				limit,
				query,
				sorting: sortingInbox,
				selectedItems,
				selectType,
				assignStatus,
				listId: id ? +id : undefined,
				filter,
				inclusionStatus: inclusionStatus ?? undefined,
				exclusionReasons,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
				signalFilterStatus: signalFilterStatus ?? undefined,
			});

			if (
				// @ts-expect-error - showSaveFilePicker is not typed
				window.showSaveFilePicker
			) {
				const downloadPromise = downloadSelected(downloadPayload).unwrap();

				const sanitizedFileName = data?.[0]?.list?.name
					? `${data[0].list.name
							.replace(/[<>:"/\\|?*]/g, "_") // Remove invalid filename characters
							.replace(/\s+/g, "_") // Replace spaces with underscores
							.replace(/\./g, "_") // Replace dots with underscores
							.replace(/'/g, "_") // Replace single quotes with underscores
							.trim()}.xlsx`
					: "listexport.xlsx";

				window
					// @ts-expect-error - showSaveFilePicker is not typed
					.showSaveFilePicker({
						suggestedName: sanitizedFileName,
						types: [
							{
								description: "My File",
								accept: {
									["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]: [`.xlsx`],
								},
							},
						],
					})
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.then((handle: { createWritable: () => Promise<any> }) =>
						handle.createWritable().then(async (writable) => {
							const blob = await downloadPromise;

							await writable.write(blob);
							await writable.close();
						})
					)
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						console.error(error);
					});
			} else {
				const downloadPromise = await downloadSelected(downloadPayload).unwrap();

				const sanitizedFileName = data?.[0]?.list?.name
					? `${data[0].list.name
							.replace(/[<>:"/\\|?*]/g, "_") // Remove invalid filename characters
							.replace(/\s+/g, "_") // Replace spaces with underscores
							.replace(/\./g, "_") // Replace dots with underscores
							.replace(/'/g, "_") // Replace single quotes with underscores
							.trim()}.xlsx`
					: "listexport.xlsx";

				const url = URL.createObjectURL(downloadPromise);

				const a = document.createElement("a");

				a.href = url;

				a.download = sanitizedFileName;

				a.rel = "noopener";

				// Firefox is happier when the element is in the DOM
				document.body.appendChild(a);
				a.click();
				a.remove();

				// Revoke the URL after a tick to let the download start
				setTimeout(() => URL.revokeObjectURL(url), 1000);
			}
		} catch (error) {
			console.error(error);
		}
	};

	/** Adds the picked leads to the picked tracker and answers whether it did (PREC-4497). */
	const handleAddToTracker = async (): Promise<boolean> => {
		const listId = selectedTracker?.id;

		if (!listId) {
			return trackerAddHint.refuse("pickTracker");
		}

		const listPersonIds = getBulkAssignParam({
			data: data ?? [],
			selectedItems: selectedItems ?? [],
			selectedItemForConfirmation,
		}).map((row) => row.listPersonId);

		if (listPersonIds.length === 0) {
			return trackerAddHint.refuse("selectLead");
		}

		const request = {
			listId: +listId,
			listPersonIds,
			selectType: selectType,
			filter: filter,
			inclusionFilterStatus: inclusionStatus ?? undefined,
			priorityFilterStatus: priorityFilterStatus ?? undefined,
			// The tab's statuses for an older API; the whole view the table showed (PREC-4450).
			assignStatus: bulkStatusScope.assignStatuses,
			...selectAllRequestScope(bulkStatusScope),
		};

		// An older API answers a failure with 200 { status: "failed" } (PREC-4497).
		return runOrToast(
			async () =>
				assertBulkAddSucceeded(
					addOrganizationOnly && !selectedListIsJobChange
						? await bulkAddCompanyToTracker(request).unwrap()
						: await bulkAddToTracker(request).unwrap()
				),
			t("inbox.trackerFooter.addFailed")
		);
	};

	const handleAddToWebhook = async () => {
		if (!data) {
			return;
		}

		if (!selectedWebhook) {
			return;
		}

		const webhookId = selectedWebhook.id;

		if (!webhookId) {
			return;
		}

		const listPersonIds = selectedItems;

		if (!listPersonIds) {
			return;
		}

		let param: Array<{ listPersonId: number }> = [];

		if (selectedItemForConfirmation) {
			const cObj = data.find((item) => item.id === selectedItemForConfirmation.id);

			if (cObj) {
				param = [
					{
						listPersonId: selectedItemForConfirmation.id,
					},
				];
			}
		} else {
			param = selectedItems
				.map((id) => {
					const cObj = data.find((item) => item.id === id);

					if (!cObj) {
						return undefined;
					}

					return {
						listPersonId: id,
					};
				})
				.filter(Boolean) as Array<{ listPersonId: number }>;
		}

		if (param.length > 0) {
			await bulkAddToWebhook({
				webhookId: +webhookId,
				listPersonIds: param.map((p) => p.listPersonId),
				selectType: selectType,
				filter: filter,
				inclusionFilterStatus: inclusionStatus ?? undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
				// The tab's statuses for an older API; the whole view the table showed (PREC-4450).
				assignStatus: bulkStatusScope.assignStatuses,
				...selectAllRequestScope(bulkStatusScope),
			}).unwrap();
		}
	};

	const handleBulkStatusChange = async ({
		assignStatus,
		newInclusionStatus,
		snoozedUntil,
		snoozed,
	}: {
		assignStatus?: AssignStatus;
		newInclusionStatus?: InclusionStatus;
		snoozedUntil?: Date;
		snoozed?: boolean;
	}) => {
		if (!data) {
			return;
		}

		const param = getBulkAssignParam({ data, selectedItems, selectedItemForConfirmation });

		if (param.length > 0) {
			const moved = await bulkChangeStatus({
				listId: id ? +id : 0,
				assign: param,
				assignStatus: assignStatus ?? undefined,
				inclusionStatus: newInclusionStatus ?? undefined,
				...bulkStatusScope,
				snoozedUntil: snoozedUntil ?? undefined,
				snoozed: snoozed ?? undefined,
			});

			if (!moved) {
				return;
			}
		}

		handleOpenSnackbar(ts("bulkStatusChangeSuccess"));
		setSelectedItemForConfirmation(null);
	};

	/** The rows a blacklist action covers: the one picked from a row's menu, else the selection. */
	const blacklistTargets = leadsByIds(
		data ?? [],
		selectedItemForConfirmation ? [selectedItemForConfirmation.id] : selectedItems
	);

	/**
	 * Blacklists the picked row or the selection on the server, which writes every lead's entry and
	 * moves them once (PREC-4435); answers whether it did.
	 */
	const handleBulkAddToBlacklist = async (): Promise<boolean> => {
		const request = buildBulkBlacklistRequest({
			listId: id ? +id : 0,
			pickedRowId: selectedItemForConfirmation?.id,
			selectedItems,
			scope: bulkStatusScope,
			addCompany: addEntireCompanyToBlacklist,
		});
		const done = await runOrToast(() => bulkBlacklist(request).unwrap(), t("blacklist.addFailed"));

		if (!done) {
			return false;
		}

		handleOpenSnackbar(ts("addToBlackListSuccess"));

		setSelectedItemForConfirmation(null);
		setAddEntireCompanyToBlacklist(false);

		return true;
	};

	/** Scope every bulk contact-info lookup sends — the same for all three. */
	const bulkContactInfoScope = {
		listId: id ? +id : undefined,
		selectType: selectType,
		inclusionFilterStatus: inclusionStatus ?? undefined,
		priorityFilterStatus: priorityFilterStatus ?? undefined,
		assignStatus: assignStatus ?? undefined,
		filter: filter,
		...selectAllRequestScope(bulkStatusScope),
	};

	// The lookups and the lead scoring retrigger, behind the credit-confirm modal (PREC-4263).
	const bulkCreditActions = useBulkCreditActions({
		data,
		selectedItems,
		selectedItemForConfirmation,
		clearItemForConfirmation: () => setSelectedItemForConfirmation(null),
		scope: bulkContactInfoScope,
		selectedCount: selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length,
		notify: handleOpenSnackbar,
		resetSelection: () => {
			if (selectAll) {
				toggleSelectAll();
			}

			setSelectType(SelectType.CURRENT_PAGE);
		},
	});

	const handleBulkRunAiSnippets = async () => {
		if (!data || !id) {
			return;
		}

		let param: number[] = [];

		if (selectedItemForConfirmation) {
			param.push(selectedItemForConfirmation.id);
		} else {
			const results = selectedItems
				.map((sid) => data.find((item) => item.id === sid)?.id)
				.filter((rid): rid is number => rid !== undefined);

			param = results;
		}

		try {
			const result = await bulkRunPrompt({
				ids: param,
				listId: +id,
				selectType: selectType,
				inclusionFilterStatus: inclusionStatus ?? undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
				assignStatus: assignStatus ?? undefined,
				filter: filter,
				...selectAllRequestScope(bulkStatusScope),
			}).unwrap();

			handleOpenSnackbar(
				t("inbox.runAiSnippetsSuccess", {
					queued: result.queued,
					skipped: result.skipped,
				})
			);

			if (selectAll) {
				toggleSelectAll();
			}

			setSelectedItems([]);
			setShowRunAiSnippetsModal(false);
			setSelectedItemForConfirmation(null);
			setSelectType(SelectType.CURRENT_PAGE);
		} catch {
			handleOpenSnackbar(t("inbox.runAiSnippetsError"));
			setShowRunAiSnippetsModal(false);
		}
	};

	const handleSubmitAddToCrm = useCallback(
		async (provider: CrmProvider, entities: CrmEntityType[], owner?: AddToCrmOwner) => {
			try {
				await addProspectsToCrm({
					provider,
					entities,
					ownerId: owner?.id,
					ownerName: owner?.name,
					selection: {
						source: CrmSelectionSource.LIST,
						listId: id ? +id : undefined,
						listPersonIds: selectedItems,
						selectType,
						...selectAllViewFilters(bulkStatusScope),
					},
				}).unwrap();

				handleOpenSnackbar(t("crmAddTo.success"));
				setSelectedCrmProvider(undefined);
			} catch {
				handleOpenSnackbar(t("crmAddTo.error"));
			}
		},
		[addProspectsToCrm, bulkStatusScope, handleOpenSnackbar, id, selectType, selectedItems, t]
	);

	// ── Add to another list ──────────────────────────────────────────────
	const selectionControls = { selectAll, toggleSelectAll, setSelectType, setSelectedItems };
	const addToAnotherList = useAddToAnotherList({
		listId: id ? +id : 0,
		selection: { ...selectionControls, selectedItems, selectType },
		selectionCount: selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length,
		bulkStatusScope,
		showMessage: handleOpenSnackbar,
	});

	const handleDeleteColumn = async () => {
		await deleteColumn({ id: editableColumn?.columnId }).unwrap();

		const newTableColumns = tableColumns.filter((c) => c.uid !== editableColumn?.uid);

		updateTableConfig(newTableColumns);

		setEditableColumn(undefined);
	};

	const handleReportReason = async () => {
		try {
			await createSlackMessage({
				listId: selectedItemForConfirmation?.list?.id ?? 0,
				leadId: selectedItemForConfirmation?.id ?? 0,
				companyName: selectedItemForConfirmation?.prospect?.organization?.name ?? "",
				listName: selectedItemForConfirmation?.list?.name ?? "",
				recordId: selectedItemForConfirmation?.prospect?.id ?? 0,
				leadName: selectedItemForConfirmation?.prospect?.name ?? "",
				reportReason: reportReason,
			}).unwrap();
			handleOpenSnackbar(ts("reportReasonSent"));
		} catch (e) {
			console.error(e);
		}
	};

	// Resolves whether the leads moved: a cancelled blacklist override moves nothing (PREC-2548).
	const handleBulkRevertInclusion = async (): Promise<boolean> => {
		const param = getBulkAssignParam({ data, selectedItems, selectedItemForConfirmation });
		let moved = false;

		if (param.length > 0) {
			try {
				moved = await bulkChangeStatus({
					listId: id ? +id : 0,
					assign: param,
					oldAssignStatus: AssignStatus.SAVED_FOR_LATER,
					inclusionStatus: InclusionStatus.ADDED,
					assignStatus: AssignStatus.DRAFT,
					...bulkStatusScope,
				});

				if (moved) {
					handleOpenSnackbar(ts("bulkAssignRevertSuccess"));
				}
			} catch (err) {
				console.error(err);
				handleOpenSnackbar(t("basics.error"));
			}
		}

		setSelectedItemForConfirmation(null);

		return moved;
	};

	const handleBulkUnarchive = async (targetStatus: AssignStatus = AssignStatus.DRAFT) => {
		if (!data) {
			return;
		}

		const param = getBulkAssignParam({ data, selectedItems, selectedItemForConfirmation });

		if (param.length > 0) {
			const moved = await bulkChangeStatus({
				listId: id ? +id : 0,
				assign: param,
				assignStatus: targetStatus,
				inclusionStatus: InclusionStatus.ADDED,
				oldAssignStatus: AssignStatus.ARCHIVED,
				...bulkStatusScope,
			});

			if (!moved) {
				return;
			}
		}

		handleOpenSnackbar(ts("bulkUnarchiveSuccess"));

		setSelectedItemForConfirmation(null);
	};

	const handleBulkRevertCampaignAssignment = async (assignStatus: AssignStatus) => {
		if (!data) {
			return;
		}

		const param = getBulkAssignParam({
			data,
			selectedItems,
			selectedItemForConfirmation,
		});

		if (param.length > 0) {
			const moved = await bulkChangeStatus({
				listId: id ? +id : 0,
				assign: param,
				assignStatus,
				inclusionStatus: InclusionStatus.ADDED,
				oldAssignStatus: AssignStatus.ASSIGNED,
				...bulkStatusScope,
			});

			if (!moved) {
				return;
			}
		}

		handleOpenSnackbar(ts("bulkAssignRevertSuccess"));

		setSelectedItemForConfirmation(null);
	};

	useEffect(() => {
		if (!selectAll) {
			setSelectType(SelectType.CURRENT_PAGE);
		}

		const newDataIds = data?.map((item) => item.id) || [];
		const oldDataIds = oldData?.map((item) => item.id) || [];

		if (
			newDataIds.length !== oldDataIds.length ||
			!newDataIds.every(function (value, index) {
				return value === oldDataIds[index];
			}) ||
			oldSelectAll !== selectAll
		) {
			if (selectAll) {
				handleSelectAll(data?.map((item) => item.id) || []);
			} else if (selectedItems.length > 0) {
				handleSelectAll([]);
			}

			setOldSelectAll(selectAll);

			setOldData(data);
		}
	}, [selectAll, data]);

	const [previousColumnCount, setPreviousColumnCount] = useState(0);

	// Reconcile after a fetch or earlier save finishes; without rows retain stored custom columns.
	useEffect(() => {
		if (
			tableConfiguration?.listId === Number(id) &&
			tableConfiguration?.config &&
			!isUpdatingLoading
		) {
			updateTableConfig(tableColumns, !data.length && tableConfig.length ? tableConfig : undefined);
		}
	}, [tableConfig, tableConfiguration]);

	const updateTableConfig = useCallback(
		(columns: TableColumn[], storedConfig?: TItem[]) => {
			const newConfig = storedConfig ?? buildTableConfigItems(columns);

			if (
				tableConfiguration?.config &&
				!areTableConfigsEqual(newConfig, tableConfiguration.config)
			) {
				updateConfig({
					id: tableConfiguration?.id,
					listId: id ? +id : 0,
					config: newConfig,
				});
			}
		},

		[tableConfig, tableConfiguration, id, updateConfig]
	);

	// The freshly-built default columns (order + default visibility), BEFORE any persisted user
	// config is applied. `resetTableConfig` reads these so a reset/migration restores the real
	// defaults instead of re-serialising the user's current (merged) layout.
	const defaultTableColumns: TableColumn[] = useMemo(() => {
		// The Outreach column (reach-out button + menu) is inserted centrally so it appears on every
		// list type, not just the general leads table. The CRM badges + contact-channel icons render
		// in the Name column (threaded into each config via `onOpenDetail`).
		let columns: TableColumn[] = insertOutreachColumn(
			getColumnsForListType({
				listType,
				data,
				selectAll,
				setShowAll,
				toggleSelectAll,
				selectedItems,
				handleSelectItem,
				handleCopyClick,
				ts,
				sortingInbox,
				setShowEdit,
				showEdit,
				integrations: { IRL_COUNTRIES_SUM, LEAD_SCORING },
				handleSelectCampaignAndProspect,
				assignStatus,
				inclusionStatus,
				onResearchClick: handleOpenResearchTab,
				onOpenDetail: handleOpenDetailForRow,
				exclusionReasonActions: { onEnrichAnyway: openEnrichAnywayForRow, onRetry: retryRow },
				tsContacts,
			}),
			{ ts, onOpenChat: handleOpenChatForRow }
		);

		if (data && data.length > 0) {
			for (const rec of data) {
				if (rec.score) {
					for (const score of rec.score) {
						const hasColumn = columns.find((c) => c.uid === `${score.id}-${score.name}`);

						if (!hasColumn) {
							const title = score.name;
							const width = "w-[150px]";
							const uid = `${score.id}-${score.name}`;
							const show = true;
							const moveable = true;
							const hideable = true;
							const orderTitle = `${score.id}-score`;

							const orderStatus = getOrderStatus(sortingInbox, orderTitle);

							const render = (row: ListPerson) => {
								const foundScore = row.score?.find((s) => s.id === score.id);

								return (
									<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
										<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
											<div>{foundScore?.scoreValue}</div>
										</Stack>
									</Stack>
								);
							};

							columns = [
								...columns.slice(0, 6),
								{ title, uid, orderTitle, width, render, moveable, hideable, show, orderStatus },
								...columns.slice(6),
							];
						}
					}
				}

				if (rec.list?.columns) {
					for (const customColumn of rec.list.columns) {
						const hasColumn = columns.find((c) => c.uid === `custom-${customColumn.id}`);

						if (!hasColumn) {
							const type = customColumn.type as CustomColumnTypes;
							const title = customColumn.name;
							const width = "fit-content";
							const minWidth = "100px";
							const uid = `custom-${customColumn.id}`;
							const show = true;
							const moveable = true;
							const hideable = true;
							const orderTitle = `custom-${customColumn.id}`;
							const currentSvgIcon = getSvgIcons(type);

							const orderStatus = getOrderStatus(sortingInbox, orderTitle);

							const render = (row: ListPerson) => {
								const currentCustomColumn = row.list.columns?.find((c) => c.id === customColumn.id);

								const currentEvent = row.events?.find(
									(e) => e.aggregateId === `${row.id}-${row.list.id}-${customColumn.id}`
								);

								const columnValue = row.columns?.find(
									(c) => c.columnConfigId === currentCustomColumn?.id
								);

								return (
									<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
										<Stack
											alignItems="flex-start"
											direction="column"
											justifyContent="flex-start"
											width="100%"
										>
											{(type === CustomColumnTypes.TEXT ||
												type === CustomColumnTypes.RANDOM_VALUE ||
												type === CustomColumnTypes.AI_SNIPPET) &&
												currentCustomColumn && (
													<CustomTextColumn
														columnConfig={currentCustomColumn}
														columnValue={columnValue}
														currentEvent={currentEvent}
														customColumnTextValue={customColumnTextValue}
														row={row}
														setCustomColumnTextValue={setCustomColumnTextValue}
														setShowEdit={setShowEdit}
														showEdit={showEdit}
														type={type}
													/>
												)}
											{(type === CustomColumnTypes.GOOGLE_NEWS ||
												type === CustomColumnTypes.JOB_CHANGE ||
												type === CustomColumnTypes.JOB_OFFER ||
												type === CustomColumnTypes.AI_PROMPT ||
												type === CustomColumnTypes.LEAD_SCORING ||
												type === CustomColumnTypes.LINKEDIN_POST_ENGAGEMENT ||
												type === CustomColumnTypes.TECHNOLOGY ||
												type === CustomColumnTypes.LINKEDIN_POST) &&
												currentCustomColumn && (
													<CustomTextColumnReadOnly
														columnConfig={currentCustomColumn}
														columnValue={columnValue}
														currentEvent={currentEvent}
														listPerson={row}
													/>
												)}
											{type === CustomColumnTypes.LINK && currentCustomColumn && (
												<CustomLinkColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnTextValue={customColumnTextValue}
													row={row}
													setCustomColumnTextValue={setCustomColumnTextValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}
											{type === CustomColumnTypes.NUMBER && currentCustomColumn && (
												<CustomNumberColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnNumberValue={customColumnNumberValue}
													row={row}
													setCustomColumnNumberValue={setCustomColumnNumberValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}
											{type === CustomColumnTypes.DEPARTMENT_SIZE && currentCustomColumn && (
												<CustomDepartmentSizeColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnNumberValue={customColumnNumberValue}
													row={row}
													setCustomColumnNumberValue={setCustomColumnNumberValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}

											{type === CustomColumnTypes.BOOLEAN && currentCustomColumn && (
												<CustomBooleanColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													row={row}
													type={type}
												/>
											)}
										</Stack>
									</Stack>
								);
							};

							columns = [
								...columns,
								{
									title,
									uid,
									minWidth,
									width,
									render,
									orderTitle,
									show,
									moveable,
									hideable,
									orderStatus,
									svgIcon: currentSvgIcon,
									setEditableColumn,
									setShowEditColumn,
								},
							];
						}
					}
				}

				if (rec.aiEnrichmentResultOrg) {
					for (const aiEnrichmentColumn of rec.aiEnrichmentResultOrg) {
						if (!aiEnrichmentColumn.aiEnrichment?.name) {
							continue;
						}

						const hasColumn = columns.find(
							(c) => c.uid === `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`
						);

						if (!hasColumn) {
							const title = aiEnrichmentColumn.aiEnrichment.name;
							const width = "fit-content";
							const minWidth = "100px";
							const uid = `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`;
							const show = true;
							const moveable = true;
							const hideable = true;
							const orderTitle = `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`;
							const orderStatus = getOrderStatus(sortingInbox, orderTitle);

							const render = (row: ListPerson) => {
								const correctAiEnrichmentColumn = row.aiEnrichmentResultOrg?.find(
									(a) => a.aiEnrichmentId === aiEnrichmentColumn.aiEnrichmentId
								);

								return (
									<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
										<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
											{correctAiEnrichmentColumn?.results}
										</Stack>
									</Stack>
								);
							};

							columns = [
								...columns,
								{
									title,
									uid,
									minWidth,
									width,
									render,
									orderTitle,
									orderStatus,
									show,
									moveable,
									hideable,
								},
							];
						}
					}
				}

				if (rec.aiEnrichmentResultPerson) {
					for (const aiEnrichmentColumn of rec.aiEnrichmentResultPerson) {
						if (!aiEnrichmentColumn.aiEnrichment?.name) {
							continue;
						}

						const hasColumn = columns.find(
							(c) => c.uid === `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`
						);

						if (!hasColumn) {
							const title = aiEnrichmentColumn.aiEnrichment.name;
							const width = "fit-content";
							const minWidth = "100px";
							const uid = `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`;
							const show = true;
							const moveable = true;
							const hideable = true;
							const orderTitle = `list-ai-${aiEnrichmentColumn.aiEnrichmentId}`;

							const render = (row: ListPerson) => {
								const correctAiEnrichmentColumn = row.aiEnrichmentResultPerson?.find(
									(a) => a.id === aiEnrichmentColumn.id
								);

								return (
									<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
										<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
											{correctAiEnrichmentColumn?.results}
										</Stack>
									</Stack>
								);
							};

							columns = [
								...columns,
								{ title, uid, minWidth, width, render, orderTitle, show, moveable, hideable },
							];
						}
					}
				}
			}
		}

		return columns.filter((c) => c.uid !== "personaScore" && c.uid !== "companyScore");
	}, [
		selectedItems,
		listType,
		data,
		selectAll,
		toggleSelectAll,
		handleSelectItem,
		handleCopyClick,
		ts,
		sortingInbox,
		showEdit,
		IRL_COUNTRIES_SUM,
		inclusionStatus,
		handleSelectCampaignAndProspect,
		customColumnTextValue,
		customColumnNumberValue,
	]);

	// The columns actually rendered: defaults with the user's persisted order/visibility applied.
	const tableColumns: TableColumn[] = useMemo(
		() =>
			applyPersistedColumnConfig(
				// The merge mutates column objects; keep the reset defaults pristine.
				defaultTableColumns.map((column) => ({ ...column })),
				tableConfig,
				{ viewDrivenVisibilityUids: ["exclusionReason"] }
			),
		[defaultTableColumns, tableConfig]
	);

	useEffect(() => {
		if (data.length !== 0) {
			const currentColumnCount = data[0]?.list?.columns?.length || 0;

			if (currentColumnCount !== previousColumnCount) {
				updateTableConfig(tableColumns);
				setPreviousColumnCount(currentColumnCount);
			}
		}
	}, [data, previousColumnCount, updateTableConfig]);

	// Table permissions from extracted hook
	const {
		hasItemsWithMissingEMail,
		hasItemsWithMissingPhone,
		hasAssignedItems,
		hasExcludedItems,
		hasBlacklistedItems,
		hasSnoozableItems,
		hasUnsnoozableItems,
		hasBlacklistableItems,
		hasUnarchiveableItems,
		hasUnparkableItems,
		hasParkableItems,
		hasAssignableItems,
		hasExcludableItems,
		hasArchivableItems,
		hasPlaceholderItems,
	} = useTablePermissions({ data, selectedItems, selectAll, selectType });

	const { bulkFindPerson, modal: findPersonModal } = useBulkFindPerson({
		listId: id ? +id : undefined,
		selectedItems,
		selectType,
		filter,
		inclusionStatus: inclusionStatus ?? undefined,
		priorityFilterStatus: priorityFilterStatus ?? undefined,
		assignStatus: assignStatus ?? undefined,
		...selectAllRequestScope(bulkStatusScope),
		hasPlaceholderItems,
		openSnackbar: handleOpenSnackbar,
		selection: selectionControls,
	});

	const { reachOutBulkTarget, reachOutAnchor, openReachOut, closeReachOut } = useBulkReachOut({
		data,
		listId: id ? +id : undefined,
		selectedItems,
		selectType,
		itemCount: meta?.itemCount,
		filter,
		inclusionStatus: inclusionStatus ?? undefined,
		priorityFilterStatus: priorityFilterStatus ?? undefined,
		assignStatus: assignStatus ?? undefined,
		...selectAllRequestScope(bulkStatusScope),
	});

	const { handleDeepResearchSuccess, handleDeepResearchError } = useBulkDeepResearchHandlers({
		openSnackbar: handleOpenSnackbar,
		selectAll,
		toggleSelectAll,
		setSelectType,
	});

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full overflow-auto">
			{isFetching && <LoadingOverlay />}
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						icon={faSearch}
						name={"search"}
						placeholder={ts("searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				{toolbarSlot}
				<div className="w-fit min-w-[120px] max-w-[220px]">
					{!isFetchingList && list ? (
						<DropdownWithCheckboxes
							size={ButtonSize.XS}
							data={toPriorityFilterItems((status) => ts(`priorityFilterOptions.${status}`))}
							defaultValue={
								priorityFilterStatus && priorityFilterStatus.length > 0
									? priorityFilterStatus.map((i) => {
											return {
												title: ts(`priorityFilterOptions.${i}`),
												id: i,
											};
										})
									: [
											{
												title: ts(`priorityFilterOptions.${PriorityFilterStatus.ALL}`),
												id: PriorityFilterStatus.ALL,
											},
										]
							}
							floating={true}
							portal
							handleSelect={(values: DropdownWithCheckboxesItem[]) => {
								setPage(1);

								const addedValue = values.find(
									(val) => !priorityFilterStatus.includes(val.id as PriorityFilterStatus)
								);

								const newValues = values.filter(
									(val) =>
										val.id !== PriorityFilterStatus.ALL &&
										val.id !== PriorityFilterStatus.ALL_WITH_PRIORITY
								);

								if (addedValue && addedValue.id === PriorityFilterStatus.ALL) {
									setPriorityFilterStatus([PriorityFilterStatus.ALL]);
								} else if (addedValue && addedValue.id === PriorityFilterStatus.ALL_WITH_PRIORITY) {
									setPriorityFilterStatus([PriorityFilterStatus.ALL_WITH_PRIORITY]);
								} else {
									setPriorityFilterStatus(newValues.map((v) => v.id as PriorityFilterStatus));
								}
							}}
							value={
								priorityFilterStatus && priorityFilterStatus.length > 0
									? priorityFilterStatus.map((i) => {
											return {
												title: ts(`priorityFilterOptions.${i}`),
												id: i,
											};
										})
									: [
											{
												title: ts(`priorityFilterOptions.${PriorityFilterStatus.ALL}`),
												id: PriorityFilterStatus.ALL,
											},
										]
							}
						/>
					) : (
						<Skeleton height={48} variant="rectangular" width={231.5} />
					)}
				</div>
				{list?.sourceType === ListSourceType.TRACKER && (
					<div className="w-fit min-w-[100px] max-w-[200px]">
						{!isFetchingList && list ? (
							<DropdownWithCheckboxes
								size={ButtonSize.XS}
								data={[
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
										id: SignalFilterStatus.ALL,
									},
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.ALL_WITH_SIGNAL}`),
										id: SignalFilterStatus.ALL_WITH_SIGNAL,
									},
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.S1}`),
										id: SignalFilterStatus.S1,
									},
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.S2}`),
										id: SignalFilterStatus.S2,
									},
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.S3}`),
										id: SignalFilterStatus.S3,
									},
									{
										title: ts(`signalFilterOptions.${SignalFilterStatus.DQ}`),
										id: SignalFilterStatus.DQ,
									},
								]}
								defaultValue={
									signalFilterStatus && signalFilterStatus.length > 0
										? signalFilterStatus.map((i) => {
												return {
													title: ts(`signalFilterOptions.${i}`),
													id: i,
												};
											})
										: [
												{
													title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
													id: SignalFilterStatus.ALL,
												},
											]
								}
								floating={true}
								portal
								handleSelect={(values: DropdownWithCheckboxesItem[]) => {
									setPage(1);

									const addedValue = values.find(
										(val) => !signalFilterStatus.includes(val.id as SignalFilterStatus)
									);

									const newValues = values.filter(
										(val) =>
											val.id !== SignalFilterStatus.ALL &&
											val.id !== SignalFilterStatus.ALL_WITH_SIGNAL
									);

									if (addedValue && addedValue.id === SignalFilterStatus.ALL) {
										setSignalFilterStatus([SignalFilterStatus.ALL]);
									} else if (addedValue && addedValue.id === SignalFilterStatus.ALL_WITH_SIGNAL) {
										setSignalFilterStatus([SignalFilterStatus.ALL_WITH_SIGNAL]);
									} else {
										setSignalFilterStatus(newValues.map((v) => v.id as SignalFilterStatus));
									}
								}}
								value={
									signalFilterStatus && signalFilterStatus.length > 0
										? signalFilterStatus.map((i) => {
												return {
													title: ts(`signalFilterOptions.${i}`),
													id: i,
												};
											})
										: [
												{
													title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
													id: SignalFilterStatus.ALL,
												},
											]
								}
							/>
						) : (
							<Skeleton height={48} variant="rectangular" width={231.5} />
						)}
					</div>
				)}
				<div className="w-fit min-w-[50px] max-w-[220px]">
					{!isFetchingList && list ? (
						<GroupedShowFilter
							ts={ts}
							list={list}
							statuses={inclusionStatus ?? [InclusionFilterStatus.ADDED]}
							exclusionReasons={exclusionReasons}
							onChange={(statuses, reasons) => {
								setPage(1);
								setInclusionStatus(statuses);
								setExclusionReasons(reasons);
							}}
						/>
					) : (
						<Skeleton height={48} variant="rectangular" width={231.5} />
					)}
				</div>
				<UnassignedTableActions
					ts={ts}
					columns={tableColumns}
					sortBy={urlSortBy}
					sortOrder={urlSortOrder}
					onSort={(sortParams) => {
						setPage(1);
						handleSorting(sortParams);
					}}
					onOpenColumns={() => setShowSelectColumns(true)}
					filter={filter}
					onOpenFilter={() => setShowFilterModal(true)}
				/>
			</div>

			{showAll && (
				<Modal
					handleClose={() => setShowAll(false)}
					handleSave={async () => {
						handleSetSelectType(prepSelectType);
					}}
					isOpened={showAll}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={ts("select")}
				>
					<div className="flex flex-col items-start gap-4">
						<Radio
							isChecked={prepSelectType === SelectType.ALL}
							onChange={() => {
								setPrepSelectType(SelectType.ALL);
							}}
						>
							<>
								{ts("all")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{meta?.itemCount}
								</span>
							</>
						</Radio>
						<Radio
							isChecked={prepSelectType === SelectType.CURRENT_PAGE}
							onChange={() => {
								setPrepSelectType(SelectType.CURRENT_PAGE);
							}}
						>
							<>
								{ts("currentPage")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{meta?.itemCount < meta?.limit ? meta?.itemCount : meta?.limit}
								</span>
							</>
						</Radio>
					</div>
				</Modal>
			)}
			<UnassignedAddLeadModal
				isOpen={showAddLeadModal}
				list={list}
				setIsOpen={setShowAddLeadModal}
			/>
			{showAddToTrackerModal && (
				<Modal
					handleClose={() => {
						setShowAddToTrackerModal(false);
						setAddOrganizationOnly(false);
						trackerAddHint.clear();
					}}
					handleSave={async () => {
						// A failed add, or nothing to send, says so and keeps the dialog open (PREC-4497).
						if (!(await handleAddToTracker())) {
							return;
						}

						if (selectAll) {
							toggleSelectAll();
						}

						setShowAddToTrackerModal(false);
						setAddOrganizationOnly(false);
					}}
					isLoading={bulkAddToTrackerIsLoading || bulkAddCompanyToTrackerIsLoading}
					isOpened={showAddToTrackerModal}
					size="md"
					submitButtonText={"Add"}
					title={"Add Person to Custom Signal Tracker"}
				>
					<TrackerDestinationPicker
						hint={trackerAddHint.hint}
						isFetching={isFetchingTrackerLists}
						isLoading={isLoadingTrackerLists}
						lists={combinedTrackerAndJobChangeLists}
						selectedTracker={selectedTracker}
						onSearch={setTrackerSearchValue}
						onSelect={(tracker) => {
							trackerAddHint.clear();
							setSelectedTracker(tracker);
						}}
					/>
					{!selectedListIsJobChange && (
						<div className="mb-4">
							<Checkbox
								isChecked={addOrganizationOnly}
								labelClass="cursor-pointer"
								onChange={() => setAddOrganizationOnly(!addOrganizationOnly)}
							>
								{ts("addOrganizationOnly")}
							</Checkbox>
						</div>
					)}
				</Modal>
			)}

			{showSelectColumns && tableConfig && setShowSelectColumns && (
				<ColumnModal
					handleResetToDefault={() => setTableConfig(buildTableConfigItems(defaultTableColumns))}
					setEditableColumn={setEditableColumn}
					setShowDeleteColumn={setShowDeleteColumn}
					setShowEditColumn={setShowEditColumn}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}
			{bulkCreditActions.modals}

			{showRunAiSnippetsModal && (
				<Modal
					handleClose={() => setShowRunAiSnippetsModal(false)}
					handleSave={handleBulkRunAiSnippets}
					isLoading={bulkRunPromptIsLoading}
					isOpened={showRunAiSnippetsModal}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={t("inbox.runAiSnippets")}
				>
					{selectedItemForConfirmation ? (
						<>
							{t("inbox.runAiSnippetsConfirmSingle", {
								name: selectedItemForConfirmation?.prospect?.name,
							})}
						</>
					) : (
						<>
							{t("inbox.runAiSnippetsConfirm", {
								count:
									selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length,
							})}
						</>
					)}
				</Modal>
			)}

			{showUnsnoozeModal && (
				<Modal
					handleClose={() => setShowUnsnoozeModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({ snoozed: false });
						if (selectAll) {
							toggleSelectAll();
						}

						setShowUnsnoozeModal(false);
						handleSelectAll([]);
					}}
					isOpened={showUnsnoozeModal}
					size="xs"
					submitButtonText="Unsnooze"
					title="Unsnooze Prospect"
				>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<div className="flex flex-col gap-2">
								Are you sure you want to unsnooze the{" "}
								{selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length} selected
								prospects?
							</div>
						</div>
					</div>
				</Modal>
			)}

			{showSnoozeModal && (
				<Modal
					handleClose={() => setShowSnoozeModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({ snoozedUntil, snoozed: true });
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowSnoozeModal(false);
						handleSelectAll([]);
					}}
					isOpened={showSnoozeModal}
					size="xs"
					submitButtonText="Snooze"
					title="Snooze Prospect"
				>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<div className="flex flex-col gap-2">
								<div className="flex flex-col gap-2">
									Are you sure you want to snooze the{" "}
									{selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length} selected
									prospects?
								</div>
								<InputField
									handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										setSnoozedUntil(new Date(e.target.value));
									}}
									name="snoozedUntil"
									placeholder="Snooze until"
									type="datetime-local"
									value={snoozedUntil.toISOString().substring(0, 19)}
								/>
							</div>
						</div>
					</div>
				</Modal>
			)}

			{showBlacklistModal && (
				<UnassignedBlacklistModal
					addCompany={addEntireCompanyToBlacklist}
					pickedFromRow={!!selectedItemForConfirmation}
					selectedCount={
						selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length
					}
					// A select-all's companies are not known up front: the toggle names none.
					selection={
						selectType === SelectType.ALL && !selectedItemForConfirmation
							? null
							: selectionOfLeads(blacklistTargets)
					}
					onClose={() => setShowBlacklistModal(false)}
					onConfirm={async () => {
						if (!(await handleBulkAddToBlacklist())) {
							return;
						}

						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowBlacklistModal(false);
					}}
					onToggleCompany={setAddEntireCompanyToBlacklist}
				/>
			)}
			{showCheckLasterModal && (
				<Modal
					handleClose={() => setShowCheckLasterModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({ assignStatus: AssignStatus.SAVED_FOR_LATER });
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowCheckLasterModal(false);
					}}
					isOpened={showCheckLasterModal}
					size="xs"
					submitButtonText={ts("saveForLater")}
					title={ts("saveForLater")}
				>
					{selectedItemForConfirmation ? (
						<>
							{ts("saveForLaterConfirmationMessage")} {selectedItemForConfirmation?.prospect?.name}
						</>
					) : (
						<>
							Add {selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length} selected
							items to review later?
						</>
					)}
				</Modal>
			)}
			{showArchiveModal && (
				<Modal
					handleClose={() => setShowArchiveModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({
							assignStatus: AssignStatus.ARCHIVED,
						});
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowArchiveModal(false);
					}}
					isOpened={showArchiveModal}
					size="xs"
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("archive")}
					title={"Ignore Leads"}
				>
					{selectedItemForConfirmation ? (
						<>{ts("archiveConfirmationMessage")}</>
					) : (
						<>
							Ignore {selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length}{" "}
							selected leads from the list?
						</>
					)}
				</Modal>
			)}
			{showExcludeFromListModal && (
				<Modal
					handleClose={() => setShowExcludeFromListModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({
							newInclusionStatus: InclusionStatus.UNSURE,
							assignStatus: AssignStatus.DRAFT,
						});
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowExcludeFromListModal(false);
					}}
					isOpened={showExcludeFromListModal}
					size="xs"
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("excludeFromList")}
					title={ts("excludeFromList")}
				>
					<ExcludeFromListConfirmation
						count={selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length}
						single={!!selectedItemForConfirmation}
					/>
				</Modal>
			)}
			{showAddToListModal && (
				<Modal
					handleClose={() => setShowAddToListModal(false)}
					handleSave={async () => {
						await handleBulkStatusChange({ newInclusionStatus: InclusionStatus.ADDED });
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowAddToListModal(false);
					}}
					isOpened={showAddToListModal}
					size="xs"
					submitButtonText={ts("addToList")}
					title={ts("addToList")}
				>
					{selectedItemForConfirmation ? (
						<>{ts("addToListConfirmationMessage")}</>
					) : (
						<>
							Add {selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length} selected
							items to the list?
						</>
					)}
				</Modal>
			)}
			{showRevertModal && (
				<Modal
					handleClose={() => setShowRevertModal(false)}
					handleSave={async () => {
						await handleBulkRevertInclusion();
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowRevertModal(false);
					}}
					isOpened={showRevertModal}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={ts("confirmRevertAssignment")}
				>
					{selectedItemForConfirmation ? (
						<>
							{ts("revertAssignment")} for {selectedItemForConfirmation?.prospect?.name}?
						</>
					) : (
						<>
							{ts("revertAssignment")} for all{" "}
							{selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length} selected
							items?
						</>
					)}
				</Modal>
			)}

			{showUnarchiveModal && (
				<Modal
					handleClose={() => setShowUnarchiveModal(false)}
					handleSave={async () => {
						await handleBulkUnarchive(unarchiveTarget);
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowUnarchiveModal(false);
					}}
					isOpened={showUnarchiveModal}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={
						unarchiveTarget === AssignStatus.SAVED_FOR_LATER
							? ts("confirmUnarchiveToReview")
							: ts("confirmUnarchiveToDraft")
					}
				>
					{selectedItemForConfirmation ? (
						<>
							{unarchiveTarget === AssignStatus.SAVED_FOR_LATER
								? ts("unarchiveToReviewAssignment")
								: ts("unarchiveToDraftAssignment")}{" "}
							for {selectedItemForConfirmation?.prospect?.name}?
						</>
					) : (
						<>
							{unarchiveTarget === AssignStatus.SAVED_FOR_LATER
								? ts("unarchiveToReviewAssignment")
								: ts("unarchiveToDraftAssignment")}{" "}
							for all {selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length}{" "}
							selected items?
						</>
					)}
				</Modal>
			)}

			{showRevertCampaignAssignmentModal && (
				<Modal
					handleClose={() => setShowRevertCampaignAssignmentModal(false)}
					handleSave={async () => {
						await handleBulkRevertCampaignAssignment(AssignStatus.DRAFT);
						if (selectAll) {
							toggleSelectAll();
						}

						setSelectType(SelectType.CURRENT_PAGE);
						setShowRevertCampaignAssignmentModal(false);
					}}
					isOpened={showRevertCampaignAssignmentModal}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={"Revert Campaign Assignment"}
				>
					<Alert
						icon={<Icon icon={faExclamationTriangle} />}
						severity="error"
						title="Revert Campaign Assignment"
					>
						{selectedItems.length <= 3 ? (
							<>
								Are you sure you want to revert the campaign assignment for
								<b className="ml-1">
									{data
										.filter((p) => selectedItems.includes(p.id))
										.map((p) => p.prospect?.name)
										.join(", ")}
								</b>
								?{" "}
								<div className="underline font-bold mt-2">
									Make sure that {selectedItems.length > 1 ? "these" : "this"} prospect has not yet
									been contacted.
								</div>
							</>
						) : (
							`Are you sure you want to revert the campaign assignment for all ${
								selectType === SelectType.ALL ? meta?.itemCount : selectedItems.length
							} selected items? Make sure that these prospects have not yet
						been contacted.`
						)}
					</Alert>
				</Modal>
			)}
			{showNewColumn && id && (
				<CreateCustomColumnModal
					filters={currentViewFilters(bulkStatusScope)}
					list={list}
					listId={+id}
					listType={listType}
					open={showNewColumn}
					priorityFilterStatus={priorityFilterStatus}
					selectedProspectIds={selectedItems}
					setOpen={setShowNewColumn}
				/>
			)}
			{showDeleteColumn && id && editableColumn?.columnId && (
				<Modal
					handleClose={() => setShowDeleteColumn(false)}
					handleSave={handleDeleteColumn}
					isLoading={deleteLoading}
					isOpened={showDeleteColumn}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmation")}</div>
					<TextField
						fullWidth
						defaultValue={editableColumn.content}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}
			{showReportRecordModal && (
				<Modal
					handleClose={() => setShowReportRecordModal(false)}
					handleSave={() => {
						handleReportReason();
						setShowReportRecordModal(false);
					}}
					isOpened={showReportRecordModal}
					size="xs"
					submitButtonText={ts("reportRecord")}
					title={ts("reportRecord")}
				>
					<div className="flex flex-col">
						<span className="mb-4 text-ssm text-gray-700 ">{ts("specifyReason")}</span>
						<TextArea
							handleChange={(e) => setReportReason(e.target.value)}
							name="reportReason"
							placeholder={ts("specifyProblem")}
							showError={false}
							value={reportReason}
						/>
					</div>
				</Modal>
			)}

			{showDetailView &&
				selectedListPerson &&
				(![ListSourceType.MAPS].includes(selectedListPerson.list.sourceType) ||
					([ListSourceType.MAPS].includes(selectedListPerson.list.sourceType) &&
						selectedListPerson.list.scrapeLevel === ScrapeLevel.GET_ENRICHMENT)) && (
					<DetailView
						listId={selectedListPerson.list.id}
						listPerson={data.find((row) => row.id === selectedListPerson.id)}
						openInChat={openChatFor !== null && openChatFor === selectedListPerson.id}
						setShowDetailView={setShowDetailView}
						signalFilterStatus={signalFilterStatus}
					/>
				)}

			{showDetailView &&
				selectedListPerson &&
				[ListSourceType.MAPS].includes(selectedListPerson.list.sourceType) &&
				selectedListPerson.list.scrapeLevel === ScrapeLevel.GET_INFO_AT && (
					<InfoAtGoogleMapsDetailView
						listPerson={data.find((row) => row.id === selectedListPerson.id)}
						setShowDetailView={setShowDetailView}
					/>
				)}

			{showEditColumn && id && editableColumn?.columnId && (
				<EditCustomColumnModalWrapper
					columnId={editableColumn?.columnId}
					filters={currentViewFilters(bulkStatusScope)}
					list={list}
					listId={+id}
					open={showEditColumn}
					priorityFilterStatus={priorityFilterStatus}
					selectedProspectIds={selectedItems}
					setOpen={setShowEditColumn}
				/>
			)}
			{showFilterModal && id && (
				<FilterModal
					assignStatus={assignStatus}
					filter={filter}
					listId={+id}
					listType={listType}
					setFilter={(e) => {
						setPage(1);
						setFilter(e);
					}}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}
			<div className="relative w-full pr-0">
				<div className="-pb-4">
					<Table
						addColumn={{ onAdd: () => setShowNewColumn(true), label: ts("addColumnCta") }}
						addRow={{ onAdd: () => setShowAddLeadModal(true), label: ts("addLeadCta") }}
						addToBlacklist={
							hasBlacklistableItems
								? {
										addToBlacklist: () => {
											setShowBlacklistModal(true);
										},
									}
								: undefined
						}
						addToList={
							hasExcludedItems
								? {
										addToList: () => {
											setShowAddToListModal(true);
										},
										addToListIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						addToAnotherList={
							selectedItems.length > 0
								? {
										...addToAnotherList.actions,
										excludeListId: id ? +id : undefined,
									}
								: undefined
						}
						addToCrm={
							selectedItems.length > 0
								? {
										onSelectProvider: setSelectedCrmProvider,
										isLoading: isAddToCrmLoading,
									}
								: undefined
						}
						addToProfileTracker={
							selectedItems.length > 0
								? {
										addToProfileTracker: () => {
											setShowAddToTrackerModal(true);
										},
										addToProfileTrackerIsLoading: bulkAddToTrackerIsLoading,
									}
								: undefined
						}
						addToWebhook={
							selectedItems.length > 0
								? {
										addToWebhook: () => {
											setShowWebhookModal(true);
										},
									}
								: undefined
						}
						archive={
							hasArchivableItems
								? {
										archive: () => {
											setShowArchiveModal(true);
										},
										archiveIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						columns={tableColumns}
						data={data || []}
						disqualify={
							hasExcludableItems
								? {
										disqualify: () => {
											setShowExcludeFromListModal(true);
										},
										disqualifyIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						exportIsLoading={downloadIsFetching}
						filter={filter}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						handleExport={handleExport}
						handleFetchPage={(page) => {
							if (selectAll) {
								toggleSelectAll();
								setSelectType(SelectType.CURRENT_PAGE);
							}

							setPage(page);
						}}
						handleRowClick={!showDetailView ? handleRowClick : undefined}
						handleSorting={(sortParams: SortParams) => {
							setPage(1);
							handleSorting(sortParams);
						}}
						hidePagination={false}
						isLoading={isFetching}
						limit={limit}
						noDataText={noDataNode}
						overallCount={meta?.itemCount}
						page={page}
						parked={
							hasParkableItems
								? {
										parked: () => {
											setShowCheckLasterModal(true);
										},
									}
								: undefined
						}
						removeFromBlacklist={
							hasBlacklistedItems
								? {
										// The override modal is the confirmation (PREC-2548): it names who comes off,
										// who stays on, and asks for the company where one blocks a lead.
										removeFromBlacklist: async () => {
											if (await handleBulkRevertInclusion()) {
												if (selectAll) {
													toggleSelectAll();
												}

												setSelectType(SelectType.CURRENT_PAGE);
											}
										},
										removeFromBlacklistIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						searchForEmail={
							hasItemsWithMissingEMail
								? {
										searchForEmail: () => bulkCreditActions.openLookup("EMAIL"),
										searchForEmailIsLoading: bulkCreditActions.isLoading.EMAIL,
									}
								: undefined
						}
						searchForPhone={
							hasItemsWithMissingPhone
								? {
										searchForPhone: () => bulkCreditActions.openLookup("PHONE"),
									}
								: undefined
						}
						searchForCompanyEmailAndPhone={
							selectedItems.length > 0
								? {
										searchForCompanyEmailAndPhone: () =>
											bulkCreditActions.openLookup("COMPANY_CONTACT"),
										searchForCompanyEmailAndPhoneIsLoading:
											bulkCreditActions.isLoading.COMPANY_CONTACT,
									}
								: undefined
						}
						leadScoring={
							LEAD_SCORING && selectedItems.length > 0
								? {
										leadScoring: bulkCreditActions.openLeadScoring,
										leadScoringIsLoading: bulkCreditActions.isLoading.leadScoring,
									}
								: undefined
						}
						deepResearch={
							DEEP_RESEARCH && selectedItems.length > 0
								? {
										deepResearch: () => {
											setShowDeepResearchModal(true);
										},
										deepResearchIsLoading: executeBulkDeepResearchListIsLoading,
									}
								: undefined
						}
						bulkFindPerson={bulkFindPerson}
						clearSelection={() => setSelectedItems([])}
						enrichAnyway={footerEnrichAnyway}
						reachOut={
							(hasAssignableItems || hasAssignedItems) && reachOutBulkTarget
								? { onOpen: openReachOut }
								: undefined
						}
						selectedCount={
							selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length
						}
						showFooterFunctions={true}
						snooze={
							hasSnoozableItems
								? {
										snooze: () => {
											setShowSnoozeModal(true);
										},
									}
								: undefined
						}
						tableConfig={tableConfig}
						totalPages={meta?.pageCount}
						unarchive={
							hasUnarchiveableItems
								? {
										unarchive: () => {
											setUnarchiveTarget(AssignStatus.DRAFT);
											setShowUnarchiveModal(true);
										},
										unarchiveIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						unarchiveToReview={
							hasUnarchiveableItems
								? {
										unarchiveToReview: () => {
											setUnarchiveTarget(AssignStatus.SAVED_FOR_LATER);
											setShowUnarchiveModal(true);
										},
										unarchiveToReviewIsLoading: bulkChangeStatusIsLoading,
									}
								: undefined
						}
						unpark={
							hasUnparkableItems
								? {
										unpark: () => {
											setShowRevertModal(true);
										},
									}
								: undefined
						}
						unsnooze={
							hasUnsnoozableItems
								? {
										unsnooze: () => {
											setShowUnsnoozeModal(true);
										},
									}
								: undefined
						}
						sticky={stickyHeader}
					/>
				</div>
			</div>
			{renderSnackbar()}
			{renderMessageSnackbar()}
			{renderEnrichAnywayModals()}
			{findPersonModal}
			{leaveBlacklistModal}
			{/* Bulk "Reach out" menu — anchored to the footer button, opened via the footer reachOut prop */}
			<BulkReachOutMenu
				anchorRect={reachOutAnchor}
				showMessage={handleOpenSnackbar}
				target={reachOutBulkTarget}
				onClose={closeReachOut}
				onRevertAssignedCampaign={
					hasAssignedItems ? () => setShowRevertCampaignAssignmentModal(true) : undefined
				}
				onRunAiSnippets={
					selectedItems.length > 0 ? () => setShowRunAiSnippetsModal(true) : undefined
				}
			/>
			{renderModal(handleAddToWebhook, selectAll, toggleSelectAll)}
			<AddToCrmModal
				isLoading={isAddToCrmLoading}
				isOpen={!!selectedCrmProvider}
				provider={selectedCrmProvider}
				selectedCount={
					selectType === SelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length
				}
				onClose={() => setSelectedCrmProvider(undefined)}
				onSubmit={handleSubmitAddToCrm}
			/>

			{showDeepResearchModal && (
				<DeepResearchModal
					onSuccess={handleDeepResearchSuccess}
					onError={handleDeepResearchError}
					isOnlySelected
					isAllSelected={selectType === SelectType.ALL}
					open={showDeepResearchModal}
					setOpen={setShowDeepResearchModal}
					list={list}
					listId={list?.id ?? 0}
					selectedProspectIds={selectedItems}
					priorityFilterStatus={priorityFilterStatus}
					// The tab's statuses and the search the table fetched its rows with (PREC-4443).
					filters={{
						assignStatus: bulkStatusScope.assignStatuses,
						filter,
						inclusionFilterStatus: inclusionStatus ?? undefined,
						...selectAllRequestScope(bulkStatusScope),
					}}
				/>
			)}
			{addToAnotherList.modal}
		</div>
	);
};
